import { Card, BlockStack, InlineStack, Text, SkeletonBodyText, Banner } from '@shopify/polaris';
import { useDashboard } from '../hooks/useDashboard';
import { StatusBadge } from './StatusBadge';
import { PortalButton } from './PortalButton';

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}

export function Dashboard() {
  const { data, isLoading, isError } = useDashboard();

  if (isLoading) {
    return (
      <Card>
        <SkeletonBodyText lines={3} />
      </Card>
    );
  }
  if (isError || !data) {
    return <Banner tone="critical">Could not load Unifd status. Try again shortly.</Banner>;
  }

  const { connection, lastSync } = data;

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            {connection.label}
          </Text>
          <StatusBadge status={connection.status} />
        </InlineStack>
        <Text as="p" tone="subdued">
          {connection.shop_domain}
        </Text>
        {lastSync ? (
          <Text as="p">
            {lastSync.outcome === 'success'
              ? `Synced ${lastSync.count} ${lastSync.entity}${lastSync.count === 1 ? '' : 's'} ${timeAgo(lastSync.completed_at)}`
              : `Last sync failed ${timeAgo(lastSync.completed_at)} — open Unifd portal for details`}
          </Text>
        ) : (
          <Text as="p" tone="subdued">
            No syncs yet
          </Text>
        )}
        <PortalButton status={connection.status} connectionId={connection.id} />
      </BlockStack>
    </Card>
  );
}
